import type { EditImageSaveTarget } from '@/types/edit-image';
import { useAppStore } from '@/stores/appStore';
import { useOrgContextStore } from '@/stores/orgContextStore';

export const IMAGE_EDIT_TASK_STORAGE_KEY = 'jointo:image-edit-tasks:v2:';
export const IMAGE_EDIT_TASK_CHANGE_EVENT = 'jointo:image-edit-tasks-changed';
export const IMAGE_EDIT_PRESENTATION_TTL = 24 * 60 * 60 * 1000;

export interface TrackedImageEditTask {
  jobId: string;
  scope: string;
  target: EditImageSaveTarget;
  createdAt: number;
  completedAt?: number;
  retryCount: number;
  presentationRetryCount: number;
  missingStatusCount: number;
  lastStatusResponseAt: number;
  notified: { saved?: boolean; final?: boolean };
  canvasNodeId?: string;
  /** 旧会话候选：先由后端验证账号/组织，才能迁移和呈现。 */
  legacy?: boolean;
}

const memory = new Map<string, Map<string, TrackedImageEditTask>>();
const inFlight = new Set<string>();
const ignoredLegacy = new Set<string>();
const LEGACY_KEY = 'jointo:image-edit-tasks:v1';
let storageAvailable = true;

export function getImageEditTaskScope(): string | null {
  const userId = useAppStore.getState().user?.userId;
  if (!userId) return null;
  return (
    encodeURIComponent(userId) +
    ':' +
    encodeURIComponent(useOrgContextStore.getState().activeOrgId ?? '__personal__')
  );
}

function canPersist(): boolean {
  // 无跨标签互斥能力时只用当前标签内存，不冒险恢复另一标签正在处理的任务。
  return typeof window !== 'undefined' && Boolean(navigator.locks?.request) && storageAvailable;
}

function taskMap(scope: string): Map<string, TrackedImageEditTask> {
  let tasks = memory.get(scope);
  if (!tasks) {
    tasks = new Map();
    memory.set(scope, tasks);
  }
  return tasks;
}

function validTarget(value: unknown): value is EditImageSaveTarget {
  if (!value || typeof value !== 'object' || !('kind' in value)) return false;
  const target = value as Record<string, unknown>;
  const nonempty = (...keys: string[]) =>
    keys.every((key) => typeof target[key] === 'string' && Boolean(target[key]));
  switch (target.kind) {
    case 'storyboard-image':
      return nonempty('storyboardId', 'planId', 'sourceImageId');
    case 'project-resource':
      return (
        nonempty('projectId', 'elementTagId', 'sourceResourceId') &&
        ['character', 'location', 'prop'].includes(String(target.resourceType))
      );
    case 'footage-file':
      return nonempty('projectId', 'sourceResourceId');
    case 'markup-reference':
      return nonempty('projectId', 'storyboardId', 'sourceNodeId', 'sourceResourceId');
    default:
      return false;
  }
}

export function readTrackedImageEditTasks(scope = getImageEditTaskScope()): TrackedImageEditTask[] {
  if (!scope || typeof window === 'undefined') return [];
  if (canPersist()) {
    try {
      const prefix = IMAGE_EDIT_TASK_STORAGE_KEY + scope + ':';
      const records = new Map<string, TrackedImageEditTask>();
      const settled = new Set<string>();
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key?.startsWith(prefix)) continue;
        try {
          const item = JSON.parse(localStorage.getItem(key) ?? 'null') as
            | (TrackedImageEditTask & { finishedAt?: number })
            | null;
          if (item?.scope === scope && typeof item.jobId === 'string' && item.finishedAt) {
            settled.add(item.jobId);
            continue;
          }
          if (
            item?.scope === scope &&
            typeof item.jobId === 'string' &&
            key === prefix + item.jobId &&
            Number.isFinite(item.createdAt) &&
            validTarget(item.target)
          ) {
            records.set(item.jobId, {
              ...item,
              completedAt: Number.isFinite(item.completedAt) ? item.completedAt : undefined,
              retryCount: Math.max(0, Number(item.retryCount) || 0),
              presentationRetryCount: Math.max(0, Number(item.presentationRetryCount) || 0),
              missingStatusCount: Math.max(0, Number(item.missingStatusCount) || 0),
              lastStatusResponseAt: Math.max(0, Number(item.lastStatusResponseAt) || 0),
              notified: item.notified && typeof item.notified === 'object' ? item.notified : {},
            });
          }
        } catch {
          /* 单条损坏不能阻断其他任务；不扫描旧的无身份 sessionStorage。 */
        }
      }
      memory.set(scope, records);
      for (const jobId of settled) ignoredLegacy.add(scope + ':' + jobId);
    } catch {
      storageAvailable = false;
    }
  }
  const records = taskMap(scope);
  try {
    const candidates: unknown = canPersist()
      ? JSON.parse(sessionStorage.getItem(LEGACY_KEY) ?? '[]')
      : [];
    if (Array.isArray(candidates))
      for (const item of candidates) {
        if (
          item &&
          typeof item.jobId === 'string' &&
          Number.isFinite(item.createdAt) &&
          validTarget(item.target) &&
          !records.has(item.jobId) &&
          !ignoredLegacy.has(scope + ':' + item.jobId)
        ) {
          records.set(item.jobId, {
            ...item,
            scope,
            legacy: true,
            retryCount: 0,
            presentationRetryCount: 0,
            missingStatusCount: 0,
            lastStatusResponseAt: 0,
            notified: {},
          });
        }
      }
  } catch {
    /* 旧记录损坏不影响当前任务。 */
  }
  return [...records.values()];
}

export function dismissLegacyImageEditTask(scope: string, jobId: string, adopted = false): void {
  ignoredLegacy.add(scope + ':' + jobId);
  if (!adopted) taskMap(scope).delete(jobId);
  if (adopted) {
    try {
      const candidates = JSON.parse(sessionStorage.getItem(LEGACY_KEY) ?? '[]');
      if (Array.isArray(candidates))
        sessionStorage.setItem(
          LEGACY_KEY,
          JSON.stringify(candidates.filter((item) => item?.jobId !== jobId))
        );
    } catch {
      /* 已迁移任务保留在新记录；旧候选不会覆盖新记录。 */
    }
  }
  window.dispatchEvent(new Event(IMAGE_EDIT_TASK_CHANGE_EVENT));
}

function storeTask(task: TrackedImageEditTask): void {
  taskMap(task.scope).set(task.jobId, task);
  if (canPersist()) {
    try {
      localStorage.setItem(
        IMAGE_EDIT_TASK_STORAGE_KEY + task.scope + ':' + task.jobId,
        JSON.stringify(task)
      );
    } catch {
      storageAvailable = false;
    }
  }
  window.dispatchEvent(new Event(IMAGE_EDIT_TASK_CHANGE_EVENT));
}

export function updateTrackedImageEditTask(
  scope: string,
  jobId: string,
  patch: Partial<TrackedImageEditTask>
): void {
  const current = readTrackedImageEditTasks(scope).find((item) => item.jobId === jobId);
  if (current) storeTask({ ...current, ...patch, jobId, scope });
}

export function removeTrackedImageEditTask(scope: string, jobId: string): void {
  taskMap(scope).delete(jobId);
  if (canPersist()) {
    try {
      // 只保留结束凭据，移除目标与所有跟踪数据；防止另一标签的旧会话候选复活任务。
      localStorage.setItem(
        IMAGE_EDIT_TASK_STORAGE_KEY + scope + ':' + jobId,
        JSON.stringify({ scope, jobId, finishedAt: Date.now() })
      );
    } catch {
      storageAvailable = false;
    }
  }
  window.dispatchEvent(new Event(IMAGE_EDIT_TASK_CHANGE_EVENT));
}

export function trackImageEditTask(
  task: Pick<TrackedImageEditTask, 'jobId' | 'target'>,
  scope = getImageEditTaskScope()
): void {
  if (!scope || typeof window === 'undefined') return;
  storeTask({
    ...task,
    scope,
    createdAt: Date.now(),
    retryCount: 0,
    presentationRetryCount: 0,
    missingStatusCount: 0,
    lastStatusResponseAt: 0,
    notified: {},
  });
}

/** Web Locks 原子领取；只靠 storage 事件不能排除多标签同时钉图。 */
export async function withImageEditTaskLock(
  scope: string,
  jobId: string,
  work: () => Promise<void>
): Promise<void> {
  const key = IMAGE_EDIT_TASK_STORAGE_KEY + scope + ':' + jobId;
  if (navigator.locks?.request && storageAvailable) {
    let entered = false;
    try {
      await navigator.locks.request(key, async () => {
        entered = true;
        await work();
      });
      return;
    } catch (error) {
      if (entered) throw error; // 业务异常仍交给协调器，不能重复执行同一次放置。
      storageAvailable = false; // 浏览器拒绝锁：停止持久化，降级为当前标签内存。
    }
  }
  if (inFlight.has(key)) return;
  inFlight.add(key);
  try {
    await work();
  } finally {
    inFlight.delete(key);
  }
}
